import { useState, useEffect, useCallback } from 'react'
import { Clock, RefreshCcw, Loader2 } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { getLastDeliveredMatrix } from '@/supabase-client'
import { toast } from 'sonner'

function heatClass(days) {
  if (days === null) return ''
  if (days <= 3) return 'bg-green-100'
  if (days <= 7) return 'bg-amber-50'
  if (days <= 14) return 'bg-orange-100'
  if (days <= 30) return 'bg-orange-200'
  if (days <= 60) return 'bg-red-100'
  return 'bg-red-200'
}

function abbreviate(customerKey) {
  // customerKey is "Name" or "Name|Branch"
  const parts = customerKey.split('|')
  const name = parts[0]
  const branch = parts[1]
  const firstWord = name.split(' ')[0]
  return branch ? `${firstWord} ${branch}` : firstWord
}

export default function LastDeliveredPopover() {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getLastDeliveredMatrix()
      setData(result)
    } catch (err) {
      console.error('[LastDeliveredPopover] Error loading data:', err)
      toast.error('Failed to load delivery data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    loadData()
  }, [open, loadData])

  const hasData = data && data.products.length > 0 && data.customers.length > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <Clock className="w-4 h-4 mr-2" />
          Last Delivered
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        className="w-auto p-0 bg-white"
        style={{ maxWidth: 'calc(100vw - 32px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Last Delivered (days ago)</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-7 w-7 p-0"
          >
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Content */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-8 px-16">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !hasData ? (
            <div className="py-8 px-4 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No delivery data</p>
            </div>
          ) : (
            <div>
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-20 bg-white px-3 py-2 text-left font-medium text-muted-foreground border-b border-r min-w-[100px]">
                      Product
                    </th>
                    {data.customers.map(custKey => (
                      <th
                        key={custKey}
                        className="sticky top-0 z-10 bg-white px-2 py-2 text-center font-medium text-muted-foreground border-b min-w-[60px]"
                        title={custKey.replace('|', ' — ')}
                      >
                        {abbreviate(custKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.products.map(strain => (
                    <tr key={strain} className="border-b border-border/30">
                      <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium border-r whitespace-nowrap">
                        {strain}
                      </td>
                      {data.customers.map(custKey => {
                        const days = data.matrix[strain]?.[custKey]
                        return (
                          <td
                            key={custKey}
                            className={`px-2 py-1.5 text-center tabular-nums ${heatClass(days)}`}
                          >
                            {days === null ? (
                              <span className="text-muted-foreground/40">-</span>
                            ) : (
                              days
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </PopoverContent>
    </Popover>
  )
}
